import { useState, useEffect } from 'react';

// Note: parseVars and computeValue are duplicated from useCensusData.
// Future refactor: extract to src/data/censusUtils.js.
const CENSUS_BASE = 'https://api.census.gov/data';
const API_KEY = process.env.REACT_APP_CENSUS_API_KEY;

function parseVars(varExpr) {
  if (!varExpr) return [];
  return varExpr.split('+').map(v => v.trim());
}

function computeValue(obj, numVars, denVars, unit) {
  const numerator = numVars.reduce((sum, v) => sum + (parseFloat(obj[v]) || 0), 0);
  const denominator = denVars.length > 0
    ? denVars.reduce((sum, v) => sum + (parseFloat(obj[v]) || 0), 0)
    : null;

  let value;
  if (denominator !== null && denominator > 0) {
    value = unit === 'percent' ? (numerator / denominator) * 100 : numerator / denominator;
  } else if (denominator === null) {
    value = numerator;
  } else {
    value = null;
  }
  return value !== null && value < 0 ? null : value;
}

/**
 * Fetches the value for a specific census tract for a given year.
 * The geoid format is state(2) + county(3) + tract(6) = 11 digits.
 */
async function fetchTractValueForYear(indicator, geoid, year) {
  const state  = geoid.slice(0, 2);
  const county = geoid.slice(2, 5);
  const tract  = geoid.slice(5);       // 6-digit tract code

  const numVars = parseVars(indicator.numeratorVar);
  const denVars = parseVars(indicator.denominatorVar);
  const allVars = [...new Set([...numVars, ...denVars])];

  const params = new URLSearchParams({
    get: ['NAME', ...allVars].join(','),
    for: `tract:${tract}`,
    in: `state:${state} county:${county}`,
    ...(API_KEY ? { key: API_KEY } : {})
  });

  const res = await fetch(`${CENSUS_BASE}/${year}/acs/acs5?${params}`);
  if (!res.ok) return null;

  const rows = await res.json();
  if (!Array.isArray(rows) || rows.length < 2) return null;

  const headers = rows[0];
  const obj = {};
  headers.forEach((h, i) => { obj[h] = rows[1][i]; });
  return computeValue(obj, numVars, denVars, indicator.unit);
}

/**
 * Fetches per-year ACS values for a selected census tract.
 * Only processes census indicators; CDC data is not available at tract level.
 *
 * Returns:
 *   Map<indicatorId, { trend: [{ year, value }] }> | null
 *   (null when no tract is selected)
 */
export function useSelectedTractTrend(config, selectedTract) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!selectedTract?.geoid) {
      setData(null);
      return;
    }

    let cancelled = false;

    async function load() {
      const results = new Map();
      const censusIndicators = config.indicators.filter(i => i.source === 'census');

      // Each indicator x each year = ~5 calls per indicator, all parallel per indicator
      await Promise.all(censusIndicators.map(async (indicator) => {
        const years = indicator.years ?? [];
        const trendPoints = await Promise.all(
          years.map(async (year) => {
            const val = await fetchTractValueForYear(indicator, selectedTract.geoid, year);
            return val !== null ? { year, value: val } : null;
          })
        );
        results.set(indicator.id, { trend: trendPoints.filter(Boolean) });
      }));

      if (!cancelled) setData(results);
    }

    load();
    return () => { cancelled = true; };
  }, [config, selectedTract]);

  return data;
}
